import { useState, useEffect, useRef } from 'react'
import { DateRange, RangeKeyDict } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { X, Calendar, ChevronRight } from 'lucide-react'

interface DateRangePickerProps {
  checkIn: string
  checkOut: string
  nights: number
  errors: { [key: string]: string }
  onChange: (name: string, value: string) => void
  showHeading?: boolean
  isOpen?: boolean
  onClose?: () => void
}

const formatChipDate = (value: string | Date | null) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const DateRangePickerNew = ({
  checkIn,
  checkOut,
  errors,
  onChange,
  showHeading = true,
  isOpen: externalIsOpen,
  onClose,
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [months, setMonths] = useState(2)
  const dateRangeRef = useRef<HTMLDivElement>(null)
  const [dateRange, setDateRange] = useState({
    startDate: checkIn ? new Date(checkIn) : new Date(),
    endDate: checkOut ? new Date(checkOut) : (null as Date | null),
    key: 'selection',
  })

  useEffect(() => {
    const updateMonths = () => {
      setMonths(window.innerWidth < 768 ? 1 : 2)
    }
    updateMonths()
    window.addEventListener('resize', updateMonths)
    return () => window.removeEventListener('resize', updateMonths)
  }, [])

  useEffect(() => {
    if (isOpen && dateRangeRef.current) {
      const replaceArrows = () => {
        const buttons = dateRangeRef.current?.querySelectorAll('.rdrNextPrevButton')
        buttons?.forEach((button, index) => {
          const htmlButton = button as HTMLElement
          if (htmlButton.dataset.customArrow === 'true') return
          htmlButton.dataset.customArrow = 'true'
          htmlButton.innerHTML = ''
          const isPrev = index % 2 === 0
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
          svg.setAttribute('class', 'w-4 h-4 text-gray-600')
          svg.setAttribute('fill', 'none')
          svg.setAttribute('stroke', 'currentColor')
          svg.setAttribute('viewBox', '0 0 24 24')
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
          path.setAttribute('stroke-linecap', 'round')
          path.setAttribute('stroke-linejoin', 'round')
          path.setAttribute('stroke-width', '2')
          path.setAttribute('d', isPrev ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7')
          svg.appendChild(path)
          htmlButton.appendChild(svg)
        })
      }

      const timer = setTimeout(replaceArrows, 50)
      const observer = new MutationObserver(replaceArrows)
      if (dateRangeRef.current) {
        observer.observe(dateRangeRef.current, { childList: true, subtree: true })
      }

      return () => {
        clearTimeout(timer)
        observer.disconnect()
      }
    }
  }, [isOpen, months])

  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setIsOpen(externalIsOpen)
    }
  }, [externalIsOpen])

  useEffect(() => {
    if (checkIn) {
      setDateRange((prev) => ({
        ...prev,
        startDate: new Date(checkIn),
      }))
    }
  }, [checkIn])

  useEffect(() => {
    if (checkOut) {
      setDateRange((prev) => ({
        ...prev,
        endDate: new Date(checkOut),
      }))
    } else {
      setDateRange((prev) => ({
        ...prev,
        endDate: null,
      }))
    }
  }, [checkOut])

  const handleSelect = (ranges: RangeKeyDict) => {
    const selection = ranges.selection
    if (selection.startDate) {
      setDateRange({
        startDate: selection.startDate,
        endDate: selection.endDate || null,
        key: 'selection',
      })

      onChange('checkIn', selection.startDate.toISOString().split('T')[0])

      if (selection.endDate) {
        onChange('checkOut', selection.endDate.toISOString().split('T')[0])
      } else {
        onChange('checkOut', '')
      }
    }
  }

  const handleClear = () => {
    const today = new Date()
    setDateRange({
      startDate: today,
      endDate: null,
      key: 'selection',
    })
    onChange('checkIn', '')
    onChange('checkOut', '')
  }

  const handleClose = () => {
    setIsOpen(false)
    onClose?.()
  }

  const handleDone = () => {
    if (dateRange.startDate && dateRange.endDate) {
      handleClose()
    }
  }

  const calculatedNights =
    dateRange.startDate && dateRange.endDate
      ? Math.ceil(
          (dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
        )
      : 0

  const checkInLabel = formatChipDate(dateRange.startDate) || 'Check-in'
  const checkOutLabel = formatChipDate(dateRange.endDate) || 'Check-out'
  const hasRange = !!(dateRange.startDate && dateRange.endDate)

  if (!isOpen && !showHeading) {
    return null
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[9998]"
          onClick={handleClose}
        />
      )}

      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-0.5rem)] sm:max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Calendar className="w-4 h-4 text-primary-600 shrink-0" />
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                  Select your dates
                </h3>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Date chips */}
            <div className="px-4 py-2 flex gap-2 shrink-0 border-b border-gray-50">
              <div
                className={`flex-1 min-w-0 rounded-lg border px-3 py-1.5 ${
                  dateRange.startDate && !dateRange.endDate
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Check-in</p>
                <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{checkInLabel}</p>
              </div>
              <div className="flex items-center text-gray-300">
                <ChevronRight className="w-4 h-4" />
              </div>
              <div
                className={`flex-1 min-w-0 rounded-lg border px-3 py-1.5 ${
                  dateRange.endDate ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Check-out</p>
                <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{checkOutLabel}</p>
              </div>
            </div>

            {/* Calendar */}
            <div className="flex-1 min-h-0 px-1 sm:px-3 py-1 overflow-hidden" ref={dateRangeRef}>
              <DateRange
                ranges={[dateRange]}
                onChange={handleSelect}
                moveRangeOnFirstSelection={false}
                months={months}
                direction="horizontal"
                rangeColors={['#D4A657']}
                minDate={new Date()}
                showDateDisplay={false}
                showMonthAndYearPickers={true}
                showPreview={true}
                editableDateInputs={false}
                dragSelectionEnabled={true}
                preventSnapRefocus={true}
                calendarFocus="backwards"
                className="w-full custom-date-range compact-date-range"
              />
            </div>

            {(errors.checkIn || errors.checkOut) && (
              <div className="px-4 pb-1 shrink-0">
                {errors.checkIn && <p className="text-red-500 text-xs">{errors.checkIn}</p>}
                {errors.checkOut && <p className="text-red-500 text-xs">{errors.checkOut}</p>}
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50/80 shrink-0">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Clear dates
                  </button>
                  {hasRange && calculatedNights > 0 && (
                    <p className="text-[11px] text-primary-700 font-medium mt-0.5">
                      {calculatedNights} night{calculatedNights !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDone}
                    disabled={!dateRange.startDate || !dateRange.endDate}
                    className="px-4 py-1.5 text-xs sm:text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DateRangePickerNew
